import * as React from "react";
import { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import {
  DialogTitle,
  Typography,
  Box,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import axios from "axios";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

export default function EditCreditsModal({
  open,
  setOpen,
  user,
  getAllUser,
}) {
  const [credits, setCredits] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setCredits(user.credits ?? "");
    }
  }, [user]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async () => {
    if (credits === "" || isNaN(Number(credits))) {
      Swal.fire({
        text: "Please enter a valid credit amount.",
        icon: "warning",
        customClass: "modal_class_success",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `https://backend.whalesai.com/api/v1/admin/user-updates/${user._id}`,
        {
          credits: Number(credits),
        },
        {
          headers: {
            authorization: `Bearer ${localStorage.getItem("AiAdmin")}`,
          },
        }
      );

      if (response.status === 200) {
        handleClose();
        Swal.fire({
          html: `<p class='text-break text-white'>Credits updated successfully.</p>`,
          icon: "success",
          customClass: "modal_class_success",
        });
        getAllUser();
      } else {
        Swal.fire({
          text: "Failed to update credits.",
          icon: "error",
          background: "#011225",
        });
      }
    } catch (error) {
      Swal.fire({
        text: "An error occurred while updating credits.",
        icon: "error",
        background: "#011225",
      });
      console.error("Error updating credits:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      scroll="body"
      aria-labelledby="edit-credits-dialog-title"
      open={open}
    >
      <Box
        sx={{
          backgroundColor: "#3080ab",
          color: "#fff",
          width: {
            lg: "500px",
          },
        }}
      >
        <DialogTitle
          sx={{ m: 0, p: 2, borderBottom: "2px solid #9a9ead54" }}
          id="edit-credits-dialog-title"
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2 text-white">
              <Typography variant="h6">Edit Credits</Typography>
            </div>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: "absolute",
                right: 8,
                color: "white",
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              padding: 2,
              backgroundColor: "#fff",
              color: "#000",
              borderRadius: "8px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              mt: 1,
            }}
          >
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              User: {user?.name}
            </Typography>
            <Typography variant="body2" sx={{ color: "#555", mb: 2 }}>
              Email: {user?.email}
            </Typography>

            <Typography variant="body2" sx={{ color: "red", mb: 2 }}>
              <strong>Current Credits:</strong> {user?.credits}
            </Typography>

            <TextField
              label="New Credits"
              type="number"
              fullWidth
              variant="outlined"
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              size="small"
              InputProps={{
                inputProps: { min: 0 },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              color: "#fff",
              borderColor: "#fff",
              "&:hover": { borderColor: "#ccc", color: "#ccc" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#ffc107",
              color: "#000",
              "&:hover": { backgroundColor: "#e0a800" },
            }}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Box>
    </BootstrapDialog>
  );
}
